import { httpBatchLink, httpLink, createTRPCProxyClient, splitLink } from "@trpc/client"
import { createTRPCReact } from "@trpc/react-query"
import superjson from "superjson"

import { getDevSession } from "./dev-session"
import { supabase } from "./supabase"

// Type-only import - this won't include runtime code in the bundle
import type { AppRouter } from "../../../aixinternal-api/src/router/_app"

/**
 * Create tRPC React hooks with proper typing
 */
export const trpc = createTRPCReact<AppRouter>()

/**
 * Get the API base URL based on environment
 */
function getApiBaseUrl(): string {
  // In development, use local Vercel dev server.
  // Honor VITE_API_URL when set (dev-prep.sh writes a per-checkout offset into
  // .env.ports) so a worktree's web hits its own API instead of the base 8790.
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_API_URL || "http://localhost:8790"
  }

  // In production, use environment variable or default to api subdomain
  const apiUrl = import.meta.env.VITE_API_URL
  if (apiUrl) {
    return apiUrl
  }

  // Default to api subdomain
  const currentUrl = new URL(window.location.href)
  return `https://api-${currentUrl.hostname.replace("www.", "")}`
}

/**
 * Helper to create tRPC client for React apps
 * Call this in your main.tsx to setup ReactQuery + tRPC
 */
export const createTRPCReactClient = (): ReturnType<typeof trpc.createClient> => {
  const baseUrl = getApiBaseUrl()
  console.log("tRPC Client: Using API URL:", baseUrl)
  // The DEV inspection proxy admits one prompt mutation per request; ordinary apps retain batching.
  const requestLink = import.meta.env.VITE_GATEWAY_AUTHORITY_MODE === "true" ? httpLink : httpBatchLink

  const linkOptions = {
    url: `${baseUrl}/api/trpc`,
    headers: async () => {
      const headers: Record<string, string> = {}

      // Add API key from environment
      const apiKey = import.meta.env.VITE_API_KEY
      if (apiKey) {
        headers.Authorization = `Bearer ${apiKey}`
      }

      // Local-dev one-click login: the backend recognizes this Bearer token.
      // No-op (returns null) in production builds.
      const devSession = getDevSession()
      if (devSession) {
        headers.Authorization = `Bearer ${devSession.accessToken}`
      }

      // Add Supabase auth token if available
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession()
        if (session?.access_token) {
          headers["x-supabase-auth"] = session.access_token
          // console.log('tRPC Client: Added Supabase auth header');
        }
      } catch (error) {
        console.warn("Failed to get Supabase session for tRPC headers:", error)
      }

      // Add source header for debugging
      headers["x-trpc-source"] = "web-app"

      // console.log('tRPC Client: Request headers:', Object.keys(headers));
      return headers
    },
  }

  return trpc.createClient({
    transformer: superjson,
    links: [
      // Opt-out for heavy parallel reads: concurrent procedures in one batch share one pipelined Postgres socket
      // in workerd and can stall each other (companyHome.getOverview x2 hangs). Use trpc.context.skipBatch.
      splitLink({
        condition: (op) => op.context.skipBatch === true,
        true: httpLink({ ...linkOptions, fetch: fetchWithReadTimeout }),
        false: requestLink(linkOptions),
      }),
    ],
  })
}

/** Unbatched reads (dashboard widgets, monitor pages) give up after this, so a hung Worker shows an error, not a spinner */
const READ_TIMEOUT_MS = 30_000

/** Times out GETs (queries) only; mutations are POSTs and may legitimately run long (LLM calls, syncs). */
const fetchWithReadTimeout = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  if ((init?.method ?? "GET").toUpperCase() !== "GET") return fetch(input, init)
  const timeout = AbortSignal.timeout(READ_TIMEOUT_MS)
  const signal = init?.signal ? AbortSignal.any([init.signal, timeout]) : timeout
  return fetch(input, { ...init, signal })
}

/**
 * Vanilla tRPC client for use outside of React components
 * Useful for imperative calls in event handlers
 */
export const trpcVanilla = createTRPCProxyClient<AppRouter>({
  transformer: superjson,
  links: [
    httpBatchLink({
      url: `${getApiBaseUrl()}/api/trpc`,
      headers: async () => {
        const headers: Record<string, string> = {}

        const apiKey = import.meta.env.VITE_API_KEY
        if (apiKey) {
          headers.Authorization = `Bearer ${apiKey}`
        }

        // Local-dev one-click login (no-op in production builds).
        const devSession = getDevSession()
        if (devSession) {
          headers.Authorization = `Bearer ${devSession.accessToken}`
        }

        try {
          const {
            data: { session },
          } = await supabase.auth.getSession()
          if (session?.access_token) {
            headers["x-supabase-auth"] = session.access_token
          }
        } catch (error) {
          console.warn("Failed to get Supabase session for tRPC headers:", error)
        }

        headers["x-trpc-source"] = "web-app-vanilla"
        return headers
      },
    }),
  ],
})
